/** O campo de texto e o botão. Uma linha que cresce até seis; Enter envia,
 *  Shift e Enter quebram; enquanto a resposta chega o botão vira Parar, e parar
 *  mantém o que já veio. Acima do campo, o chip com o contexto da tela e o
 *  interruptor que o desliga para uma pergunta só. No teto diário o campo
 *  desabilita e uma linha diz quando volta. */
import { useEffect, useRef, useState, type KeyboardEvent } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Switch from '@mui/material/Switch'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { SendHorizontal, Square } from 'lucide-react'
import { useI18n, type UseI18n } from '@/core/i18n/useI18n'
import { HTML_LANG, type Lang } from '@/core/i18n/messages'
import { SP_TZ, dayISO, todayISO } from '@/shared/datetime'
import { useAssistente, useConversaDoAssistente } from './AssistenteProvider'
import { textoDoChip } from './contexto'

function fraseDoLimite(t: UseI18n['t'], lang: Lang, voltaEm: string): string {
  const instante = new Date(voltaEm)
  if (Number.isNaN(instante.getTime())) return t('assistente.limite.atingido')
  const locale = HTML_LANG[lang]
  const hora = instante.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit', timeZone: SP_TZ, hourCycle: 'h23' })
  // en-CA dá AAAA-MM-DD, o mesmo formato de todayISO, para comparar dia com dia.
  const dia = instante.toLocaleDateString('en-CA', { timeZone: SP_TZ })
  if (dia === todayISO()) return t('assistente.limite.voltaHoje', { hora })
  if (dia === dayISO(1)) return t('assistente.limite.voltaAmanha', { hora })
  const data = instante.toLocaleDateString(locale, { day: '2-digit', month: '2-digit', timeZone: SP_TZ })
  return t('assistente.limite.voltaEm', { data, hora })
}

function ChipDeContexto() {
  const { t } = useI18n()
  const { contexto, contextoLigado, setContextoLigado } = useConversaDoAssistente()
  if (!contexto) return null
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75, minWidth: 0 }}>
      <Switch
        size="small"
        checked={contextoLigado}
        onChange={(e) => setContextoLigado(e.target.checked)}
        inputProps={{ 'aria-label': t('assistente.contexto.interruptor') }}
        sx={{ ml: -0.75 }}
      />
      <Typography
        noWrap
        title={contextoLigado ? textoDoChip(t, contexto) : undefined}
        sx={{ fontSize: '0.75rem', color: contextoLigado ? 'text.secondary' : 'text.disabled', minWidth: 0 }}
      >
        {contextoLigado ? textoDoChip(t, contexto) : t('assistente.contexto.desligado')}
      </Typography>
    </Box>
  )
}

export function Composer() {
  const { t, lang } = useI18n()
  const { enviar, parar, emAndamento, limite, rascunho, consumirRascunho } = useConversaDoAssistente()
  const { focoPedido } = useAssistente()
  const [texto, setTexto] = useState('')
  const campoRef = useRef<HTMLTextAreaElement | null>(null)
  const ultimoEnvioRef = useRef('')

  // Abrir o painel pelo botão ou pelo atalho move o foco para o campo. Ao montar
  // com o painel já aberto, recarregar a página, o foco fica onde estava.
  useEffect(() => {
    if (focoPedido > 0) campoRef.current?.focus()
  }, [focoPedido])

  // Editar e reenviar, numa bolha do usuário: o texto dela vem para o campo,
  // com o foco, e a pessoa ajusta antes de mandar. Consumido na hora, para a
  // mesma edição não voltar a cada render.
  useEffect(() => {
    if (rascunho === null) return
    setTexto(rascunho)
    consumirRascunho()
    campoRef.current?.focus()
  }, [rascunho, consumirRascunho])

  // A pergunta que bateu no teto diário não foi aceita e sairia da tela junto com
  // o turno descartado: volta para o campo, que fica desabilitado até a hora dita.
  useEffect(() => {
    if (limite) setTexto((atual) => atual || ultimoEnvioRef.current)
  }, [limite])

  const bloqueado = Boolean(limite)
  const podeEnviar = texto.trim().length > 0 && !emAndamento && !bloqueado

  function submeter() {
    if (!podeEnviar) return
    ultimoEnvioRef.current = texto
    enviar(texto)
    setTexto('')
  }

  function aoTeclar(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key !== 'Enter' || e.shiftKey || e.nativeEvent.isComposing) return
    e.preventDefault()
    submeter()
  }

  return (
    <Box sx={{ px: 2, pt: 1.25, pb: 1.5, borderTop: '1px solid', borderColor: 'divider', flex: 'none' }}>
      {limite ? (
        <Typography sx={{ fontSize: '0.75rem', color: 'warning.main', fontWeight: 600, mb: 0.75 }}>
          {`${t('assistente.limite.atingido')} ${fraseDoLimite(t, lang, limite.voltaEm)}`}
        </Typography>
      ) : (
        <ChipDeContexto />
      )}
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
        <TextField
          multiline
          maxRows={6}
          size="small"
          fullWidth
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          onKeyDown={aoTeclar}
          placeholder={t('assistente.composer.placeholder')}
          disabled={bloqueado}
          inputRef={campoRef}
          slotProps={{
            htmlInput: {
              'data-assistente-composer': 'true',
              'aria-label': t('assistente.composer.rotulo'),
              'aria-describedby': 'assistente-composer-dica',
            },
          }}
          sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem', py: 1, px: 1.5 } }}
        />
        {emAndamento ? (
          <Button
            variant="outlined"
            size="small"
            color="inherit"
            onClick={parar}
            startIcon={<Square size={12} strokeWidth={2.5} />}
            sx={{ flex: 'none', minHeight: 40, color: 'text.primary', borderColor: 'divider' }}
          >
            {t('assistente.composer.parar')}
          </Button>
        ) : (
          <Button
            variant="contained"
            size="small"
            onClick={submeter}
            disabled={!podeEnviar}
            aria-label={t('assistente.composer.enviar')}
            sx={{ flex: 'none', minWidth: 44, minHeight: 40, px: 1.5 }}
          >
            <SendHorizontal size={16} strokeWidth={2.2} />
          </Button>
        )}
      </Box>
      {/* Medidas em px por extenso: no `sx` do MUI, `width: 1` quer dizer 100%. */}
      <Typography id="assistente-composer-dica" sx={{ position: 'absolute', width: '1px', height: '1px', overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
        {t('assistente.composer.dica')}
      </Typography>
    </Box>
  )
}
